import React, { useMemo } from 'react';
import {
  Grid,
  Typography,
  IconButton,
  Paper,
  Avatar,
  Chip,
  Tooltip,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import CloseIcon from '@material-ui/icons/Close';
import StoreIcon from '@material-ui/icons/Store'; // Ícone para Estoque
import SwapHorizIcon from '@material-ui/icons/SwapHoriz'; // Ícone para Transferência
import AttachMoneyIcon from '@material-ui/icons/AttachMoney'; // Ícone para Financeiro
import NotificationsIcon from '@material-ui/icons/Notifications'; // Ícone Padrão
import FiberManualRecordIcon from '@material-ui/icons/FiberManualRecord'; // Bolinha de não lido
import EventIcon from '@material-ui/icons/Event'; // Ícone para Agendamentos
import { useHistory } from 'react-router-dom';
import { goToNotificationsTable } from '../../../../utils/utils';

// Estilos customizados (Padrão MUI v4)
const useStyles = makeStyles((theme) => ({
  card: {
    padding: theme.spacing(2),
    marginBottom: theme.spacing(1),
    borderRadius: theme.spacing(1), // Bordas mais arredondadas
    cursor: 'pointer',
    position: 'relative',
    border: '1px solid #e0e0e0',
    transition: 'all 0.2s ease-in-out',
    '&:hover': {
      backgroundColor: '#fafafa',
      transform: 'translateY(-2px)',
      boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
    },
  },
  cardUnread: {
    backgroundColor: '#f0f7ff', // Azul bem claro para não lidos
  },
  cardRead: {
    backgroundColor: '#ffffff',
  },
  avatar: {
    width: theme.spacing(5),
    height: theme.spacing(5),
  },
  title: {
    fontSize: '0.9rem',
    fontWeight: 500,
    color: '#2c3e50',
    lineHeight: 1.2,
    marginBottom: theme.spacing(0.5),
  },
  titleUnread: {
    fontWeight: 700,
  },
  chipMoney: {
    height: 24,
    fontSize: '0.75rem',
    fontWeight: 600,
    marginTop: theme.spacing(0.5),
  },
  closeButton: {
    position: 'absolute',
    top: theme.spacing(0.5),
    right: theme.spacing(0.5),
    color: theme.palette.text.secondary,
    '&:hover': {
      color: theme.palette.error.main,
      backgroundColor: 'rgba(211, 47, 47, 0.04)',
    },
  },
}));

// Utilitário para formatar moeda
const formatCurrency = (value) => {
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
  }).format(value);
};

const SlideNotifications = (props) => {
  const { notification, closeNotification, handleClose } = props;

  const classes = useStyles();
  const history = useHistory();
  const isUnread = notification.viewed === '0';

  // Define cores e ícones baseados no conteúdo e tipo
  const typeConfig = useMemo(() => {
    const text = (notification.information || '').toLowerCase();
    if (text.includes('estoque'))
      return {
        icon: <StoreIcon />,
        color: '#1976d2',
        bg: '#e3f2fd',
        label: 'Estoque',
      };
    if (text.includes('transferência'))
      return {
        icon: <SwapHorizIcon />,
        color: '#ed6c02',
        bg: '#fff3e0',
        label: 'Transferência',
      };
    if (text.includes('pagar') || text.includes('retirada') || text.includes('sangria'))
      return {
        icon: <AttachMoneyIcon />,
        color: '#d32f2f',
        bg: '#ffebee',
        label: 'Financeiro',
      };
    if (text.includes('agendamento'))
      return {
        icon: <EventIcon />,
        color: '#9c27b0', // Roxo
        bg: '#f3e5f5',
        label: 'Agendamento',
      };
    return {
      icon: <NotificationsIcon />,
      color: '#757575',
      bg: '#f5f5f5',
      label: 'Aviso',
    };
  }, [notification.information]);

  const handleNavigate = () => {
    const { type } = notification;
    const { id, scheduleId } = notification.message || {};

    if (scheduleId) {
      history.push('/home/agendamentos/agenda');
    } else if (id) {
      const URL = goToNotificationsTable(type, id);
      history.push(URL);
    }

    if (handleClose) handleClose();
    if (closeNotification)
      closeNotification(notification.id, notification.viewed);
  };

  const handleDismiss = (e) => {
    e.stopPropagation();
    if (closeNotification)
      closeNotification(notification.id, notification.viewed);
  };

  return (
    <Paper
      elevation={0}
      className={`${classes.card} ${isUnread ? classes.cardUnread : classes.cardRead
        }`}
      onClick={handleNavigate}
      style={{
        borderLeft: isUnread
          ? `4px solid ${typeConfig.color}`
          : '1px solid #e0e0e0',
      }}
    >
      <Grid container spacing={2} alignItems="flex-start" wrap="nowrap">
        {/* Ícone (Avatar) */}
        <Grid item>
          <Avatar
            className={classes.avatar}
            style={{
              backgroundColor: isUnread ? typeConfig.color : '#e0e0e0',
              color: isUnread ? '#fff' : '#757575',
            }}
          >
            {typeConfig.icon}
          </Avatar>
        </Grid>

        {/* Conteúdo de Texto */}
        <Grid item xs>
          <Grid container direction="column">
            <Grid item container justify="space-between" alignItems="center">
              <Typography
                variant="body2"
                className={`${classes.title} ${isUnread ? classes.titleUnread : ''
                  }`}
                style={{ paddingRight: 24 }}
              >
                {notification.information}

                {/* Bolinha indicadora de não lido */}
                {isUnread && (
                  <Tooltip title="Não lida">
                    <FiberManualRecordIcon
                      style={{
                        fontSize: 10,
                        color: typeConfig.color,
                        marginLeft: 8,
                        verticalAlign: 'middle',
                      }}
                    />
                  </Tooltip>
                )}
              </Typography>
            </Grid>

            {/* Valor Monetário e ID */}
            <Grid item container alignItems="center" spacing={1}>
              {notification.message?.value !== undefined && (
                <Grid item>
                  <Chip
                    label={formatCurrency(notification.message.value)}
                    size="small"
                    variant="outlined"
                    color={isUnread ? 'primary' : 'default'}
                    className={classes.chipMoney}
                  />
                </Grid>
              )}
              <Grid item>
                <Typography variant="caption" color="textSecondary">
                  ID: {notification.id}
                </Typography>
              </Grid>
            </Grid>
          </Grid>
        </Grid>

        {/* Botão de Fechar */}
        <IconButton
          size="small"
          onClick={handleDismiss}
          className={classes.closeButton}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </Grid>
    </Paper>
  );
};

export default SlideNotifications;
